import React from "react";
import ReactDOM from "react-dom";
import {Popup} from 'react-map-gl';
import bbox from '@turf/bbox';
import _ from 'lodash';
import moment from 'moment';
import pointToLineDistance from '@turf/point-to-line-distance';

import GLMap from "../glmap.js";
import Timebar from "../timebar.js";
import { statusClass, capitalize } from "../utils";
import * as secooraLines from "./secoora-convex-lines.json";

const _circleBasePaintStyle = {
  "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 3, 10, 10],
  "circle-stroke-width": 2,
  "circle-pitch-alignment": "map"
};

const circleStyle = {
  type: "circle",
  paint: {
    ..._circleBasePaintStyle,
    "circle-color": [
      "match",
      ["get", "status"],
      "baseless",
      "#fcc87f",
      "#fcc87f",
    ],
    "circle-stroke-color": [
      "match",
      ["get", "status"],
      "baseless",
      "#f6a577",
      "#f6a577",
    ]
  }
};

const circleHoverStyle = {
  type: "circle",
  paint: {
    ..._circleBasePaintStyle,
    'circle-color': "#f6a577",
    'circle-stroke-color': "#c8a360"
  }
};

const circleSelectedStyle = {
  type: "circle",
  paint: {
    ...circleHoverStyle.paint
  }
};

const circleOtherSelectedStyle = {
  type: "circle",
  paint: {
    ..._circleBasePaintStyle,
    'circle-color': "#cccccc",
    'circle-stroke-color': "#999999"
  }
}

var mountNode = document.getElementById("app");
ReactDOM.render(
  <GLMap
    idField="key"
    mapStyle={
      "mapbox://styles/secoora/cl9x479de008c14ng3coo2nk1"
    }
    mapHeight={325}
    interactiveLayers={["dots"]}
    catalogTitle="SECOORA Station Observatory"
    catalogTable={true}
    catalogTableHeight={500}
    catalogTableClasses={["station-table", "table-striped"]}
    catalogTableColumns={[
      {
        column: "label",
        label: "Station",
      },
      {
        column: "state",
        label: "State",
      },
      {
        column: "obs",
        label: "# Obs",
      },
      {
        column: "ending",
        label: "Timeline",
      },
      {
        column: "expanded",
        label: "",
        expand: true,
      },
    ]}
    layerSources={{
      url:
        "//stage-portal-cache-service.srv.axds.co/cache-service/monthlyBins?filter_id=599b441f-8b70-4304-9584-4946599f70ae",
      transform: (d) => {
        /**
         * Takes raw result, returns GLLayer:
         * {
         *    sources: JSX Source or div of Sources,
         *    catalog: [{
         *      key: string,
         *      title: <JSX>,
         *      body: <JSX>,
         *      tbd interaction
         *   }
         *   ]
         *    tbd interaction
         * }
         */

        let lower = moment
            .utc(d.metadata.minDate)
            .startOf("month")
            .startOf("day"),
          // let lower = moment.utc("2018-01-01T00:00:00Z").startOf('month').startOf('day'),
          upper = moment
            .utc(d.metadata.maxDate)
            .add(1, "months")
            .startOf("month")
            .startOf("day"),
          blankCounts = [];

        for (let start = lower.clone(); start < upper; start.add(1, "months")) {
          blankCounts.push({
            month: start.toISOString(),
            count: null,
            qcCount: {},
          });
        }

        // sort order for affiliates, custom
        let affiliateOrder = ['owner', 'publisher', 'supporter', 'affiliate'];

        let catalog = _.map(d.data, (station, si) => {
          let [lon, lat] = station.metadata.location.coordinates,
            geom = bbox(station.metadata.location),
            popupAnchor = "right",
            popupOffsetLeft = 0,
            popupOffsetTop = 0;

          let dists = _.sortBy(
              secooraLines.features.map((sl) => {
                return {
                  state: sl.properties.postal,
                  distance: pointToLineDistance(station.metadata.location, sl),
                };
              }),
              "distance"
            ),
            state = dists[0].state;

          let affiliates = _.groupBy(station.metadata.affiliations, 'agentId'),
            combinedAffiliates = _.map(affiliates, (v, k) => {
              return {
                ...v[0],
                type: _.map(v, 'type')
              }
            }).sort((a1, a2) => {
              // use custom sort order defined above to sort affiliates
              // since we've combined all "type" values, use minimum
              let a1idx = Math.min.apply(this, a1.type.map(t1 => affiliateOrder.indexOf(t1))),
                a2idx = Math.min.apply(this, a2.type.map(t2 => affiliateOrder.indexOf(t2)));

              // if not found, make sure they get worst sort order
              if (a1idx === -1) { a1idx = 99; }
              if (a2idx === -1) { a2idx = 99; }

              if (a1idx < a2idx) { return -1; }
              if (a1idx > a2idx) { return 1; }

              return 0;
            });

          // let monthData = _.fromPairs(_.map(station.counts.monthly, (mData, paramName) => {
          //   return [paramName, blankCounts.map(bc => {
          //     if (mData.length && bc.month.substring(0, 10) == mData[0].month.substring(0, 10)) {
          //       return mData.shift();
          //     }
          //     return bc;
          //   })];
          // }));

          return {
            key: station.metadata.id,
            boundingBox: geom,
            data: {
              dots: {
                type: "Feature",
                geometry: station.metadata.location,
                properties: {
                  key: station.metadata.id,
                  id: station.metadata.id,
                  label: station.metadata.label,
                },
              },
            },
            title: [
              {
                column: "label",
                el: <>{station.metadata.label}</>,
                value: station.metadata.label,
              },
              {
                column: "state",
                el: <>{state}</>,
                value: state,
              },
              {
                column: "obs",
                el: (
                  <>
                    {new Intl.NumberFormat().format(station.counts.all.count)}
                  </>
                ),
                value: station.counts.all.count,
                classes: "text-right",
              },
              {
                column: "ending",
                el: (
                  <Timebar
                    label={station.metadata.label}
                    lower={lower}
                    upper={upper}
                    start={station.metadata.feedStats.startDate}
                    end={station.metadata.feedStats.endDate}
                    fill={"#30899F"}
                  />
                ),
                value: station.metadata.feedStats.startDate,
              },
              {
                column: "expanded",
                el: (
                  <table className="table">
                    <tbody>
                      {_.map(station.counts.monthly, (mData, paramName) => {
                        return (
                          <tr key={paramName}>
                            <td style={{ paddingLeft: "20px" }}>{paramName}</td>
                            <td></td>
                            <td className={"text-right"}>
                              {new Intl.NumberFormat().format(
                                _.sumBy(mData, "count")
                              )}
                            </td>
                            <td>
                              <Timebar
                                label={station.metadata.label}
                                lower={lower}
                                upper={upper}
                                // start={mData[0].month}
                                // end={mData[mData.length - 1].month}
                                segments={mData}
                                fill={"#30899F"}
                              />

                              {/* <Sparklines
                              data={mData.map((at) => at.count)}
                              height={25}
                              limit={25}
                            >
                              <SparklinesBars
                                style={{
                                  fill: "green",
                                  fillOpacity: "0.5",
                                }}
                              />
                            </Sparklines> */}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ),
                value: "",
                expand: true,
              },
            ],
            body: (
              <>
                {
                  <>
                    <h4>Affiliates</h4>
                    <dl className="flex-dl">
                      {combinedAffiliates.map((v, i) => {
                        return (
                          <React.Fragment key={i}>
                            <dt>
                              <img
                                src={v.logoUrl}
                                alt={v.label}
                                width={24}
                                height={24}
                              />
                            </dt>
                            <dd>
                              {v.label}
                              <br />
                              {v.type.map((tt, ii) => {
                                return (
                                  <span className={"badge"} key={ii}>
                                    {capitalize(tt)}
                                  </span>
                                )
                              })}
                            </dd>
                          </React.Fragment>
                        );
                      })}
                    </dl>
                  </>
                }
              </>
            ),
            badges: (
              <>
                <div className="small">
                  Platform Type: <span className={"badge"}>
                    {capitalize(station.metadata.platformType)}
                  </span>
                </div>
                <a
                  className="btn btn-primary btn-sm"
                  href={`https://portal.secoora.org/?&sensor_version=v2cache#metadata/${station.metadata.id}/station`}
                  target="_blank"
                >
                  View in Portal <i className="glyphicon glyphicon-share"></i>
                </a>
              </>
            ),
            detailTitle: station.metadata.label,
            popup: (
              <Popup
                tipSize={10}
                anchor={popupAnchor}
                longitude={lon}
                latitude={lat}
                closeOnClick={false}
                closeButton={false}
                offsetLeft={popupOffsetLeft}
                offsetTop={popupOffsetTop}
                className={"glmap-popup"}
              >
                <div>
                  <p>{station.metadata.label}</p>
                </div>
              </Popup>
            ),
          };
        });

        return {
          name: "stations",
          styles: {
            dots: {
              normal: circleStyle,
              hovered: circleHoverStyle,
              selected: circleSelectedStyle,
              otherSelected: circleOtherSelectedStyle,
            },
          },
          catalog: catalog,
        };
      },
    }}
  />,
  mountNode
);
